<template>
  <div class="signup-container">
    <div v-if="isLoading" class="loading">
      <div class="loading-spinner"></div>
      <p>Loading...</p>
    </div>
    <div v-else-if="signupComplete" class="signup-complete">
      <h2>Signup Complete</h2>
      <p>Thank you for completing your profile, {{ user.firstName }}!</p>
      <button @click="redirectToLessons" class="primary-btn">Continue to Lessons</button>
    </div>
    <div v-else class="signup-form-container">
      <h2>Complete Your Profile</h2>
      <p>Please provide the following information to get started:</p>
      
      <form @submit.prevent="submitForm" class="signup-form">
        <div class="form-group">
          <label for="firstName">First Name*</label>
          <input 
            id="firstName" 
            v-model="formData.firstName" 
            type="text" 
            required
            :class="{ 'error': formErrors.firstName }"
          />
          <p v-if="formErrors.firstName" class="error-message">{{ formErrors.firstName }}</p>
        </div>
        
        <div class="form-group">
          <label for="lastName">Last Name*</label>
          <input 
            id="lastName" 
            v-model="formData.lastName" 
            type="text" 
            required
            :class="{ 'error': formErrors.lastName }"
          />
          <p v-if="formErrors.lastName" class="error-message">{{ formErrors.lastName }}</p>
        </div>
        
        <div class="form-group">
          <label for="email">Email*</label>
          <input 
            id="email" 
            v-model="formData.email" 
            type="email" 
            required
            :class="{ 'error': formErrors.email }"
            disabled
            title="Email address is provided by your login credentials"
          />
          <p v-if="formErrors.email" class="error-message">{{ formErrors.email }}</p>
        </div>
        
        <div class="form-group">
          <label for="college">College/University*</label>
          <input 
            id="college" 
            v-model="formData.college" 
            type="text" 
            required
            :class="{ 'error': formErrors.college }"
          />
          <p v-if="formErrors.college" class="error-message">{{ formErrors.college }}</p>
        </div>
        
        <div class="form-group">
          <label for="linkedin">LinkedIn Profile URL (Optional)</label>
          <input 
            id="linkedin" 
            v-model="formData.linkedin" 
            type="url"
            placeholder="https://www.linkedin.com/in/your-profile"
            :class="{ 'error': formErrors.linkedin }"
          />
          <p v-if="formErrors.linkedin" class="error-message">{{ formErrors.linkedin }}</p>
        </div>
        
        <div class="form-actions">
          <button type="submit" class="primary-btn" :disabled="isSubmitting">
            {{ isSubmitting ? 'Submitting...' : 'Submit' }}
          </button>
        </div>
        
        <p v-if="formErrors.general" class="error-message general-error">
          {{ formErrors.general }}
        </p>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Auth } from '@aws-amplify/auth'
import { checkSignupStatus, submitSignup } from '../services/userService'

export default {
  name: 'Signup',
  
  setup() {
    const router = useRouter()
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const signupComplete = ref(false)
    const user = ref(null)
    
    const formData = reactive({
      firstName: '',
      lastName: '',
      email: '',
      college: '',
      linkedin: ''
    })
    
    const formErrors = reactive({
      firstName: '',
      lastName: '',
      college: '',
      linkedin: '',
      general: ''
    })
    
    const checkUserSignupStatus = async () => {
      isLoading.value = true
      
      try {
        // Get user info from Cognito
        console.log('Attempting to get authenticated user...')
        const authUser = await Auth.currentAuthenticatedUser()
        console.log('Auth user:', authUser)
        
        // First try to get email from token payload since it's available there
        if (authUser.signInUserSession?.idToken?.payload?.email) {
          const email = authUser.signInUserSession.idToken.payload.email;
          console.log('Found email in token payload:', email);
          formData.email = email;
          
          // Also pre-fill first and last name if available in the token payload
          if (authUser.signInUserSession.idToken.payload.given_name) {
            formData.firstName = authUser.signInUserSession.idToken.payload.given_name;
            console.log('Pre-filling first name:', formData.firstName);
          }
          
          if (authUser.signInUserSession.idToken.payload.family_name) {
            formData.lastName = authUser.signInUserSession.idToken.payload.family_name;
            console.log('Pre-filling last name:', formData.lastName);
          }
        }
        // Then try other locations as backup
        else if (authUser && authUser.attributes) {
          console.log('Auth user attributes:', authUser.attributes)
          
          if (authUser.attributes.email) {
            console.log('Found email in attributes.email:', authUser.attributes.email);
            formData.email = authUser.attributes.email;
          } else if (authUser.username && authUser.username.includes('@')) {
            console.log('Using username as email:', authUser.username);
            formData.email = authUser.username;
          } else {
            console.log('No email found in auth user');
          }
        } else {
          console.log('Could not find user attributes or token payload with email');
        }
        
        // Check if user has already completed signup
        const response = await checkSignupStatus()
        console.log('Signup status response:', response)
        
        if (response.signupComplete && response.user) {
          signupComplete.value = true
          user.value = response.user
          
          // Pre-fill form with existing data
          formData.firstName = response.user.firstName || formData.firstName
          formData.lastName = response.user.lastName || formData.lastName
          formData.college = response.user.college || ''
          formData.linkedin = response.user.linkedin || ''
          
          // Ensure email is set
          if (response.user.email) {
            formData.email = response.user.email
            console.log('Using email from signup data:', formData.email)
          }
        }
      } catch (error) {
        console.error('Error checking signup status:', error)
        formErrors.general = 'Error checking signup status. Please try again.'
      } finally {
        isLoading.value = false
      }
    }
    
    const validateForm = () => {
      let isValid = true
      
      // Reset errors
      Object.keys(formErrors).forEach(key => {
        formErrors[key] = ''
      })
      
      // Validate first name
      if (!formData.firstName.trim()) {
        formErrors.firstName = 'First name is required'
        isValid = false
      }
      
      // Validate last name
      if (!formData.lastName.trim()) {
        formErrors.lastName = 'Last name is required'
        isValid = false
      }
      
      // Validate email
      if (!formData.email || !formData.email.trim()) {
        formErrors.email = 'Email is required'
        isValid = false
      }
      
      // Validate college
      if (!formData.college.trim()) {
        formErrors.college = 'College/University is required'
        isValid = false
      }
      
      // Validate LinkedIn URL format if provided
      if (formData.linkedin && !isValidUrl(formData.linkedin)) {
        formErrors.linkedin = 'Please enter a valid URL'
        isValid = false
      }
      
      return isValid
    }
    
    const isValidUrl = (url) => {
      try {
        new URL(url)
        return true
      } catch {
        return false
      }
    }
    
    const submitForm = async () => {
      if (!validateForm()) {
        return
      }
      
      isSubmitting.value = true
      formErrors.general = ''
      
      try {
        const response = await submitSignup(formData)
        if (response && response.user) {
          signupComplete.value = true
          user.value = response.user
        } else {
          formErrors.general = 'Unexpected server response. Please try again.'
        }
      } catch (error) {
        console.error('Error submitting signup:', error)
        formErrors.general = error.message || 'Error submitting signup. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }
    
    const redirectToLessons = () => {
      router.push('/chapter/1/lesson/1')
    }
    
    onMounted(() => {
      checkUserSignupStatus()
    })
    
    return {
      isLoading,
      isSubmitting,
      signupComplete,
      user,
      formData,
      formErrors,
      submitForm,
      redirectToLessons
    }
  }
}
</script>

<style scoped>
.signup-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.signup-form-container, .signup-complete {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

h2 {
  color: #333;
  margin-bottom: 1rem;
}

p {
  color: #666;
  margin-bottom: 1.5rem;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
}

input {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

input.error {
  border-color: #e74c3c;
}

.error-message {
  color: #e74c3c;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  margin-bottom: 0;
}

.general-error {
  text-align: center;
  margin-top: 1rem;
}

.form-actions {
  margin-top: 1rem;
}

.primary-btn {
  width: 100%;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.primary-btn:hover:not(:disabled) {
  background-color: #2980b9;
}

.primary-btn:disabled {
  background-color: #95c8ec;
  cursor: not-allowed;
}

.signup-complete {
  text-align: center;
}
</style> 